/*
 * Iter306 — Festival pass shopping basket.
 *
 * Basket state lives in localStorage so the buyer can browse away and
 * come back without losing items. Rendered as a slide-out panel that
 * hangs off the top-right of FestivalLanding.
 *
 * When ANY item is POA / £0, the basket blocks the Stripe checkout
 * and surfaces the two branches the spec requires:
 *   · Remove blocked → strip POA/£0 items, proceed to checkout
 *   · Contact us     → open enquiry form pre-filled with blocked items
 *
 * The enquiry form POSTs to `/festival/enquiries` which stores the
 * enquiry (visible in admin panel with status=new) and best-effort
 * emails the platform owner.
 */
import { useEffect, useMemo, useState } from "react";
import { ShoppingCart, Trash2, X, Mail, Zap, Tag, Check, ArrowLeft, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FestivalDetailsStep, buildFestivalDetails, detailsError } from "@/components/FestivalDetailsStep";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "rl_festival_basket_v1";

function money(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "gbp").toUpperCase(), maximumFractionDigits: 2 }).format(amount);
  } catch { return `${amount} ${currency}`; }
}

function loadBasket() {
  try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || []; }
  catch { return []; }
}
function saveBasket(items) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
}

export function useBasket(pricing) {
  const [items, setItems] = useState(loadBasket);
  useEffect(() => saveBasket(items), [items]);
  const isTier = (pt) => !!pricing?.[pt] && pricing[pt].tier !== "bolton";
  // Returns "conflict" (no change) when a different tier is already in the basket.
  const addItem = (pass_type) => {
    if (isTier(pass_type)) {
      const existing = items.find((i) => isTier(i.pass_type));
      if (existing && existing.pass_type !== pass_type) return "conflict";
      if (existing) return "exists";
      setItems((cur) => [...cur, { pass_type, qty: 1 }]);
      return "added";
    }
    setItems((cur) => {
      const found = cur.find((i) => i.pass_type === pass_type);
      if (found) return cur.map((i) => i.pass_type === pass_type ? { ...i, qty: i.qty + 1 } : i);
      return [...cur, { pass_type, qty: 1 }];
    });
    return "added";
  };
  // Swap the tier; drop self-service bolt-ons that don't fit the new tier/days.
  const replaceTier = (pass_type) => {
    const def = pricing?.[pass_type];
    setItems((cur) => [
      ...cur.filter((i) => {
        const d = pricing?.[i.pass_type];
        if (!d || isTier(i.pass_type)) return false;
        if (d.kind === "self_service") return d.for_tier === def.tier && Number(d.for_days) === Number(def.days);
        return true;
      }),
      { pass_type, qty: 1 },
    ]);
  };
  const removeItem = (pass_type) => setItems((cur) => cur.filter((i) => i.pass_type !== pass_type));
  const setQty = (pass_type, qty) => setItems((cur) =>
    qty <= 0 ? cur.filter((i) => i.pass_type !== pass_type)
             : cur.map((i) => i.pass_type === pass_type ? { ...i, qty } : i));
  const clear = () => setItems([]);

  const lines = useMemo(() => items.map((it) => {
    const def = pricing?.[it.pass_type] || null;
    const unit = Number(def?.amount || 0);
    const poa = !!def?.poa || unit <= 0;
    const bulkQty = Number(def?.bulk_qty || 0);
    const bulkAmount = Number(def?.bulk_amount || 0);
    const bundled = bulkQty > 0 && bulkAmount > 0 && it.qty >= bulkQty;
    const packs = bundled ? Math.floor(it.qty / bulkQty) : 0;
    const full = unit * it.qty;
    const total = bundled ? packs * bulkAmount + (it.qty % bulkQty) * unit : full;
    return {
      ...it, def,
      label: def?.label || it.pass_type,
      unit_amount: unit,
      line_total: Number(total.toFixed(2)),
      bundle: bundled ? { packs, bulkQty, bulkAmount, remainder: it.qty % bulkQty, saving: Number((full - total).toFixed(2)) } : null,
      currency: def?.currency || "gbp",
      poa,
    };
  }), [items, pricing]);

  const subtotal = useMemo(
    () => lines.filter((l) => !l.poa).reduce((s, l) => s + l.line_total, 0),
    [lines],
  );
  const bundleSaving = useMemo(() => lines.reduce((s, l) => s + (l.bundle?.saving || 0), 0), [lines]);
  const blocked = useMemo(() => lines.filter((l) => l.poa), [lines]);
  const currency = lines.find((l) => !l.poa)?.currency || "gbp";

  const tierLines = lines.filter((l) => l.def && l.def.tier !== "bolton");
  const mainTier = tierLines.reduce((best, l) => (!best || Number(l.def.days) > Number(best.def.days) ? l : best), null);
  const days = mainTier ? Number(mainTier.def.days) : null;
  const availableDays = useMemo(() => {
    if (!mainTier || !pricing) return [];
    return Object.values(pricing)
      .filter((p) => p.tier === mainTier.def.tier && !p.poa && Number(p.amount) > 0)
      .map((p) => Number(p.days)).sort((a, b) => a - b);
  }, [mainTier, pricing]);

  // Swap tier + per-day self-service bolt-ons to the matching N-day pass key.
  const setDays = (n) => setItems((cur) => {
    const out = [];
    for (const it of cur) {
      const def = pricing?.[it.pass_type];
      const retimable = def && (def.tier !== "bolton" || def.kind === "self_service");
      const next = retimable ? it.pass_type.replace(/_\d+day$/, `_${n}day`) : it.pass_type;
      const key = pricing?.[next] ? next : it.pass_type;
      const found = out.find((o) => o.pass_type === key);
      if (found) found.qty += it.qty; else out.push({ ...it, pass_type: key });
    }
    return out;
  });

  return { items, lines, subtotal, bundleSaving, blocked, currency, days, availableDays, addItem, replaceTier, removeItem, setQty, setDays, clear };
}

export default function FestivalBasketPanel({ pricing, basket, open, onClose }) {
  const { items, lines, subtotal, bundleSaving, blocked, currency, days, availableDays, removeItem, setQty, setDays, clear } = basket;
  const { user } = useAuth();
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null); // { code, discount_amount, final_total }
  const [applying, setApplying] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [step, setStep] = useState("review");
  const [details, setDetails] = useState({ name: "", startDate: "", endDate: "" });
  // Iter312 — Festival licence reminder at checkout. Pay button stays
  // disabled until the buyer ticks the checkbox. Mirrors the T&Cs gate
  // at organiser signup so the licence terms are surfaced twice
  // (before account creation + right before payment).
  const [termsAccepted, setTermsAccepted] = useState(false);

  const finalTotal = discount ? Math.max(0, subtotal - Number(discount.discount_amount || 0)) : subtotal;
  const isBlocked = blocked.length > 0;
  const isEmpty = items.length === 0;
  const detailsErr = days ? detailsError(details, days, availableDays) : "Add a festival tier to your basket";

  useEffect(() => {
    // Basket contents changed — re-price any applied code against the new subtotal.
    if (!discount) return;
    api.post("/festival-basket/apply-code", { code: discount.code, subtotal })
      .then((r) => setDiscount(r.data))
      .catch(() => setDiscount(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  useEffect(() => { if (isEmpty) setStep("review"); }, [isEmpty]);

  const applyCode = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setApplying(true);
    try {
      const r = await api.post("/festival-basket/apply-code", { code: code.trim(), subtotal });
      setDiscount(r.data);
      toast.success(`Code ${r.data.code} applied — ${money(r.data.discount_amount, currency)} off`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code not valid");
      setDiscount(null);
    } finally { setApplying(false); }
  };

  const checkout = async () => {
    if (!user) {
      toast.error("Sign in to check out");
      window.location.assign("/register?as=organiser");
      return;
    }
    if (isBlocked) return;
    if (step === "review") { setStep("details"); return; }
    if (detailsErr) { toast.error(detailsErr); return; }
    if (!termsAccepted) {
      toast.error("Please tick the festival licence confirmation before paying.");
      return;
    }
    setCheckingOut(true);
    try {
      const r = await api.post("/festival-basket/checkout", {
        items: items.map(({ pass_type, qty }) => ({ pass_type, qty })),
        origin_url: window.location.origin,
        promo_code: discount?.code || null,
        festival_details: buildFestivalDetails(details, days),
      });
      window.location.assign(r.data.checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setCheckingOut(false); }
  };

  const removeBlockedAndCheckout = () => {
    blocked.forEach((l) => removeItem(l.pass_type));
    // Deferred toast so the user sees the state update; actual checkout is a
    // second click — safer UX than auto-firing.
    toast.success("Blocked items removed — click Checkout to continue");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="festival-basket-panel" onClick={onClose}>
      <div className="flex-1 bg-black/60" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0A0A0A] border-l border-[#262626] text-white flex flex-col h-full overflow-hidden"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-[#262626] sticky top-0 bg-[#0A0A0A] z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#CCFF00]">Your basket</div>
            <div className="text-lg font-bold">{items.length} item{items.length === 1 ? "" : "s"}</div>
          </div>
          <button onClick={onClose} data-testid="basket-close" aria-label="Close" className="text-[#A3A3A3] hover:text-white"><X className="w-5 h-5" /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {isEmpty && (
            <div className="text-sm text-[#525252] py-16 text-center">
              Your basket is empty. Add a tier or bolt-on to get started.
            </div>
          )}
          {step === "details" && !isEmpty && (
            <>
              <button type="button" onClick={() => setStep("review")} data-testid="details-back"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-white">
                <ArrowLeft className="w-3 h-3" /> Back to basket
              </button>
              <FestivalDetailsStep form={details} setForm={setDetails} days={days}
                availableDays={availableDays} onDaysChange={setDays} error={detailsErr} />
              <div className="text-[10px] uppercase tracking-widest text-[#525252] pt-2">Order summary</div>
              {lines.map((l) => (
                <div key={l.pass_type} data-testid={`summary-line-${l.pass_type}`} className="flex justify-between text-xs border-b border-[#1a1a1a] pb-1.5">
                  <span className="text-white">{l.label} × {l.qty}{l.bundle && <span className="ml-1 text-[#CCFF00]">· bundle</span>}</span>
                  <span>{l.poa ? "POA" : money(l.line_total, l.currency)}</span>
                </div>
              ))}
            </>
          )}
          {step === "review" && lines.map((l) => (
            <div key={l.pass_type} data-testid={`basket-line-${l.pass_type}`} className={`border p-3 flex items-start gap-3 ${l.poa ? "border-[#FFB020]/60 bg-[#FFB020]/5" : "border-[#262626]"}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{l.label}</div>
                {l.poa ? (
                  <div className="text-[10px] uppercase tracking-widest text-[#FFB020] mt-0.5">POA · needs a quote</div>
                ) : l.bundle ? (
                  <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] mt-0.5" data-testid={`basket-bundle-${l.pass_type}`}>
                    {l.bundle.packs} × {l.bundle.bulkQty} for {money(l.bundle.bulkAmount, l.currency)}
                    {l.bundle.remainder > 0 && ` + ${l.bundle.remainder} × ${money(l.unit_amount, l.currency)}`}
                    {" "}· bundle applied
                  </div>
                ) : (
                  <div className="text-[10px] uppercase tracking-widest text-[#525252] mt-0.5">
                    {money(l.unit_amount, l.currency)} × {l.qty}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setQty(l.pass_type, l.qty - 1)}
                    data-testid={`basket-qty-minus-${l.pass_type}`}
                    className="w-6 h-6 border border-[#262626] hover:border-white text-xs"
                  >−</button>
                  <div className="text-sm w-6 text-center">{l.qty}</div>
                  <button
                    type="button"
                    onClick={() => setQty(l.pass_type, l.qty + 1)}
                    disabled={!!l.def && l.def.tier !== "bolton"}
                    data-testid={`basket-qty-plus-${l.pass_type}`}
                    className="w-6 h-6 border border-[#262626] hover:border-white text-xs disabled:opacity-30 disabled:cursor-not-allowed"
                  >+</button>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold">
                  {l.poa ? <span className="text-[#FFB020]">POA</span> : money(l.line_total, l.currency)}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(l.pass_type)}
                  data-testid={`basket-remove-${l.pass_type}`}
                  className="mt-1 text-[#A3A3A3] hover:text-[#FF4500] p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {step === "review" && <UpgradeNudge lines={lines} pricing={pricing} currency={currency} onSwitch={basket.replaceTier} />}
        </div>

        {!isEmpty && (
          <div className="border-t border-[#262626] px-5 py-4 space-y-3 bg-[#0A0A0A]">
            {/* Discount code */}
            <form onSubmit={applyCode} className="flex gap-2">
              <div className="flex-1 flex items-center border border-[#262626] focus-within:border-[#CCFF00] px-2 py-1.5">
                <Tag className="w-3.5 h-3.5 text-[#A3A3A3] mr-2" />
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  data-testid="basket-code-input"
                  placeholder="Voucher / discount code"
                  className="flex-1 bg-transparent text-sm outline-none"
                  maxLength={40}
                />
              </div>
              <button
                type="submit"
                disabled={applying || !code.trim()}
                data-testid="basket-code-apply"
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-40"
              >{applying ? "…" : "Apply"}</button>
            </form>

            {/* Totals */}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-[#A3A3A3]">
                <span>Subtotal</span><span data-testid="basket-subtotal">{money(subtotal, currency)}</span>
              </div>
              {bundleSaving > 0 && (
                <div className="flex justify-between text-[#CCFF00] text-xs" data-testid="basket-bundle-savings">
                  <span>Bundle savings (included)</span><span>−{money(bundleSaving, currency)}</span>
                </div>
              )}
              {discount && (
                <div className="flex justify-between text-[#CCFF00]">
                  <span>Discount ({discount.code})</span>
                  <span>−{money(discount.discount_amount, currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-white font-bold pt-1 border-t border-[#262626]">
                <span>Total to pay</span><span data-testid="basket-total">{money(finalTotal, currency)}</span>
              </div>
              {blocked.length > 0 && (
                <div className="text-[10px] uppercase tracking-widest text-[#FFB020]">
                  + {blocked.length} POA item{blocked.length === 1 ? "" : "s"} — need a quote
                </div>
              )}
            </div>

            {/* Blocked message + two-CTA */}
            {isBlocked && (
              <div className="border border-[#FFB020] bg-[#FFB020]/5 p-3 space-y-2" data-testid="basket-blocked-banner">
                <div className="text-[11px] text-[#FFDE84] leading-relaxed">
                  One or more items in your basket need a custom quote. Remove them to pay now,
                  or contact us and we'll get back to you within 24-72 hours.
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={removeBlockedAndCheckout}
                    data-testid="basket-remove-blocked"
                    className="flex-1 px-3 py-2 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
                  >Remove POA items</button>
                  <button
                    type="button"
                    onClick={() => setShowEnquiry(true)}
                    data-testid="basket-open-enquiry"
                    className="flex-1 px-3 py-2 text-[10px] uppercase tracking-wider border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black inline-flex items-center justify-center gap-1"
                  ><Mail className="w-3 h-3" /> Contact us</button>
                </div>
              </div>
            )}

            {/* Iter312 — Licence terms reminder. Copy-locked to spec. */}
            {step === "details" && (
            <label
              className="flex items-start gap-2 border border-[#262626] p-3 cursor-pointer hover:border-[#CCFF00]/60 transition-colors"
              data-testid="basket-terms-panel"
            >
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                data-testid="basket-terms-checkbox"
                className="mt-0.5 w-4 h-4 shrink-0 accent-[#CCFF00]"
              />
              <span className="text-[11px] text-[#A3A3A3] leading-relaxed">
                By completing this purchase you confirm you have read and agree to the{" "}
                <a
                  href="/legal/festival-terms"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="basket-terms-link"
                  className="text-[#CCFF00] hover:underline"
                >
                  Rally-Lab festival licence terms
                </a>
                . This licence is non-transferable and non-refundable once activated.
              </span>
            </label>
            )}

            {/* Checkout */}
            <button
              type="button"
              onClick={checkout}
              disabled={checkingOut || isBlocked || finalTotal <= 0 || (step === "details" && (!termsAccepted || !!detailsErr))}
              data-testid="basket-checkout"
              className="w-full px-4 py-3 text-sm uppercase tracking-wider font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              style={{ background: isBlocked || (step === "details" && (!termsAccepted || detailsErr)) ? "#525252" : "#CCFF00", color: "#0A0A0A" }}
            >
              <Zap className="w-4 h-4" />
              {checkingOut ? "Redirecting to Stripe…"
                : isBlocked ? "Checkout blocked"
                : step === "review" ? "Continue to festival details"
                : detailsErr ? "Complete festival details"
                : !termsAccepted ? "Tick to enable payment"
                : "Pay now"}
            </button>

            <button
              type="button"
              onClick={clear}
              data-testid="basket-clear"
              className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-white w-full text-center"
            >Clear basket</button>
          </div>
        )}
      </aside>

      {showEnquiry && (
        <EnquiryModal
          prefilledItems={blocked.length > 0 ? blocked : lines}
          onClose={() => setShowEnquiry(false)}
        />
      )}
    </div>
  );
}

function UpgradeNudge({ lines, pricing, currency, onSwitch }) {
  const tier = lines.find((l) => l.def?.tier === "standard");
  if (!tier) return null;
  const days = Number(tier.def.days);
  const ss = lines.find((l) => l.def?.kind === "self_service" && Number(l.def.for_days) === days);
  const proKey = `pro_${days}day`;
  const pro = pricing?.[proKey];
  if (!ss || !pro || pro.poa || Number(pro.amount) <= 0) return null;
  const combo = tier.unit_amount + ss.unit_amount;
  const proAmount = Number(pro.amount);
  const diff = Number((proAmount - combo).toFixed(2));
  const priceText = diff > 0 ? `for only ${money(diff, currency)} more` : diff === 0 ? "for the same price" : `and save ${money(-diff, currency)}`;
  return (
    <div className="border border-[#CCFF00]/60 bg-[#CCFF00]/5 p-3 space-y-2" data-testid="basket-upgrade-nudge">
      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" />
        <div className="text-[11px] text-white leading-relaxed" data-testid="basket-upgrade-nudge-text">
          <span className="font-bold text-[#CCFF00]">Upgrade to Pro {days}-Day {priceText}</span> — get additional helpers and more than double the player capacity.
        </div>
      </div>
      <button type="button" onClick={() => { onSwitch(proKey); toast.success(`Switched to ${pro.label || proKey}`); }}
        data-testid="basket-upgrade-switch"
        className="w-full px-3 py-2 text-[10px] uppercase tracking-wider font-bold border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors">
        Switch to {pro.label || "Pro"}
      </button>
    </div>
  );
}

function EnquiryModal({ prefilledItems, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${BACKEND}/api/festival/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim() || null,
        items: prefilledItems.map((it) => it.pass_type),
      });
      setDone(r.data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sending failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="basket-enquiry-modal">
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-[#FFB020] text-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FFB020]">POA enquiry</div>
            <div className="text-lg font-bold">Contact Rally-Lab</div>
          </div>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white" aria-label="Close" data-testid="enquiry-close"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="p-6 text-center space-y-3" data-testid="enquiry-success">
            <div className="mx-auto w-12 h-12 flex items-center justify-center rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]">
              <Check className="w-6 h-6 text-[#CCFF00]" />
            </div>
            <div className="text-lg font-semibold">Enquiry received</div>
            <div className="text-sm text-[#A3A3A3]">{done}</div>
            <button onClick={onClose} className="mt-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black" data-testid="enquiry-done-close">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Your items</label>
              <div className="mt-1 space-y-1" data-testid="enquiry-items">
                {prefilledItems.map((it) => (
                  <div key={it.pass_type} className="text-xs text-white border border-[#262626] px-2 py-1">
                    {it.label || it.pass_type}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="enquiry-name"
                className="w-full mt-1 bg-transparent border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enquiry-email"
                className="w-full mt-1 bg-transparent border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Message (optional)</label>
              <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={2000} data-testid="enquiry-message"
                className="w-full mt-1 bg-transparent border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" />
            </div>
            <button type="submit" disabled={submitting} data-testid="enquiry-submit"
              className="w-full px-4 py-2.5 text-xs uppercase tracking-wider font-bold disabled:opacity-40"
              style={{ background: "#FFB020", color: "#0A0A0A" }}>
              {submitting ? "Sending…" : "Send enquiry"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
